/**
 * DV-INF-002: Standardized Error DTOs
 *
 * Single error shape across all Demo Viewer API endpoints
 * Ensures consistent error handling and logging
 */

/**
 * Standard error DTO returned by all Demo Viewer API endpoints
 */
export interface DemoErrorDto {
  /** Machine-readable error code for programmatic handling */
  code: string;

  /** Human-readable error message (not for string matching) */
  message: string;

  /** Server-side trace ID for debugging */
  traceId?: string;

  /** Request correlation ID from X-Request-Id header */
  requestId?: string;

  /** HTTP status code for context */
  statusCode?: number;

  /** Optional additional context */
  details?: Record<string, unknown>;
}

/**
 * Standard success response wrapper
 */
export interface DemoSuccessDto<T = unknown> {
  /** Response data */
  data: T;

  /** Request correlation ID from X-Request-Id header */
  requestId?: string;

  /** Optional metadata */
  meta?: Record<string, unknown>;
}

/**
 * Standard error codes used across Demo Viewer APIs
 */
export const DEMO_ERROR_CODES = {
  // Authentication & Authorization
  UNAUTHORIZED: 'UNAUTHORIZED',
  ADMIN_ACCESS_REQUIRED: 'ADMIN_ACCESS_REQUIRED',
  PERMISSION_DENIED: 'PERMISSION_DENIED',

  // Rate Limiting
  RATE_LIMIT_EXCEEDED: 'RATE_LIMIT_EXCEEDED',
  DEMO_RATE_LIMIT_EXCEEDED: 'DEMO_RATE_LIMIT_EXCEEDED',

  // Validation
  INVALID_EXECUTION_ID: 'INVALID_EXECUTION_ID',
  INVALID_COMMIT_ID: 'INVALID_COMMIT_ID',
  INVALID_QUERY_PARAMS: 'INVALID_QUERY_PARAMS',
  MISSING_REQUIRED_FIELD: 'MISSING_REQUIRED_FIELD',

  // Resources
  EXECUTION_NOT_FOUND: 'EXECUTION_NOT_FOUND',
  COMMIT_NOT_FOUND: 'COMMIT_NOT_FOUND',
  RESOURCE_NOT_FOUND: 'RESOURCE_NOT_FOUND',

  // Database & Service
  DATABASE_ERROR: 'DATABASE_ERROR',
  SUPABASE_ERROR: 'SUPABASE_ERROR',
  SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',

  // Network & Client
  NETWORK_ERROR: 'NETWORK_ERROR',
  REQUEST_TIMEOUT: 'REQUEST_TIMEOUT',
  REQUEST_CANCELLED: 'REQUEST_CANCELLED',

  // Generic
  INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR',
} as const;

export type DemoErrorCode = typeof DEMO_ERROR_CODES[keyof typeof DEMO_ERROR_CODES];

/**
 * Create standardized error DTO
 */
export function createDemoErrorDto(
  code: DemoErrorCode,
  message: string,
  options?: {
    traceId?: string;
    requestId?: string;
    statusCode?: number;
    details?: Record<string, unknown>;
  }
): DemoErrorDto {
  return {
    code,
    message,
    traceId: options?.traceId,
    requestId: options?.requestId,
    statusCode: options?.statusCode,
    details: options?.details,
  };
}

/**
 * Create standardized success DTO
 */
export function createDemoSuccessDto<T>(
  data: T,
  options?: {
    requestId?: string;
    meta?: Record<string, unknown>;
  }
): DemoSuccessDto<T> {
  return {
    data,
    requestId: options?.requestId,
    meta: options?.meta,
  };
}

/**
 * Type guard to check if response is an error DTO
 */
export function isDemoErrorDto(response: unknown): response is DemoErrorDto {
  return (
    typeof response === 'object' &&
    response !== null &&
    'code' in response &&
    'message' in response &&
    typeof (response as any).code === 'string' &&
    typeof (response as any).message === 'string'
  );
}

/**
 * Type guard to check if response is a success DTO
 */
export function isDemoSuccessDto<T>(response: unknown): response is DemoSuccessDto<T> {
  return (
    typeof response === 'object' &&
    response !== null &&
    'data' in response
  );
}

/**
 * Map HTTP status codes to appropriate error codes
 */
export function getErrorCodeFromStatus(statusCode: number): DemoErrorCode {
  switch (statusCode) {
    case 401:
      return DEMO_ERROR_CODES.UNAUTHORIZED;
    case 403:
      return DEMO_ERROR_CODES.PERMISSION_DENIED;
    case 404:
      return DEMO_ERROR_CODES.RESOURCE_NOT_FOUND;
    case 429:
      return DEMO_ERROR_CODES.RATE_LIMIT_EXCEEDED;
    case 500:
      return DEMO_ERROR_CODES.INTERNAL_SERVER_ERROR;
    case 503:
      return DEMO_ERROR_CODES.SERVICE_UNAVAILABLE;
    default:
      return DEMO_ERROR_CODES.UNKNOWN_ERROR;
  }
}

/**
 * User-friendly error messages for display
 */
export const DEMO_ERROR_MESSAGES: Record<DemoErrorCode, string> = {
  [DEMO_ERROR_CODES.UNAUTHORIZED]: 'Authentication required. Please log in.',
  [DEMO_ERROR_CODES.ADMIN_ACCESS_REQUIRED]: 'Admin access required for this feature.',
  [DEMO_ERROR_CODES.PERMISSION_DENIED]: 'You do not have permission to access this resource.',

  [DEMO_ERROR_CODES.RATE_LIMIT_EXCEEDED]: 'Too many requests. Please try again later.',
  [DEMO_ERROR_CODES.DEMO_RATE_LIMIT_EXCEEDED]: 'Demo Viewer request limit exceeded. Please try again later.',

  [DEMO_ERROR_CODES.INVALID_EXECUTION_ID]: 'Invalid execution ID provided.',
  [DEMO_ERROR_CODES.INVALID_COMMIT_ID]: 'Invalid commit ID provided.',
  [DEMO_ERROR_CODES.INVALID_QUERY_PARAMS]: 'Invalid query parameters.',
  [DEMO_ERROR_CODES.MISSING_REQUIRED_FIELD]: 'Required field is missing.',

  [DEMO_ERROR_CODES.EXECUTION_NOT_FOUND]: 'Execution not found.',
  [DEMO_ERROR_CODES.COMMIT_NOT_FOUND]: 'Billing commit not found.',
  [DEMO_ERROR_CODES.RESOURCE_NOT_FOUND]: 'Resource not found.',

  [DEMO_ERROR_CODES.DATABASE_ERROR]: 'Database error occurred.',
  [DEMO_ERROR_CODES.SUPABASE_ERROR]: 'Supabase service error.',
  [DEMO_ERROR_CODES.SERVICE_UNAVAILABLE]: 'Service temporarily unavailable.',

  [DEMO_ERROR_CODES.NETWORK_ERROR]: 'Network connection error.',
  [DEMO_ERROR_CODES.REQUEST_TIMEOUT]: 'Request timed out.',
  [DEMO_ERROR_CODES.REQUEST_CANCELLED]: 'Request was cancelled.',

  [DEMO_ERROR_CODES.INTERNAL_SERVER_ERROR]: 'Internal server error.',
  [DEMO_ERROR_CODES.UNKNOWN_ERROR]: 'An unexpected error occurred.',
};

/**
 * Get user-friendly error message
 */
export function getDemoErrorMessage(error: DemoErrorDto | DemoErrorCode): string {
  const code = typeof error === 'string' ? error : error.code;
  return DEMO_ERROR_MESSAGES[code as DemoErrorCode] || DEMO_ERROR_MESSAGES[DEMO_ERROR_CODES.UNKNOWN_ERROR];
}